import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDeliveryVoucherJustificationOptions } from 'hooks/api/options/general';
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
} from 'components/ui/dialog';
import { ComboboxGs, TextareaGs } from 'components/ui/Forms';
import { Form, FormField } from 'components/ui/form';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components';

import { formSchema, type FormSchema } from './form-schema';
import { RejectionProfPayload } from 'contracts/delivery-vouchers/delivery-voucher-justifications';
import { useDeliveryVoucherStore } from '../../hooks/use-delivery-voucher-store';

type Ref = {
  open: () => void;
  close: () => void;
};

export type RejectionProfDialogRef = Ref;

type Props = {
  onSubmit: (payload: RejectionProfPayload, callback: () => void) => void;
  loading?: boolean;
};

export const RejectionProfDialog = React.forwardRef<Ref, Props>(
  ({ onSubmit, loading }, ref) => {
    const { deliveryVoucher } = useDeliveryVoucherStore();
    const justificationOptions = useDeliveryVoucherJustificationOptions();

    const [open, setOpen] = React.useState(false);

    const form = useForm<FormSchema>({
      resolver: zodResolver(formSchema),
    });

    /**used for handling the callback after form submission */
    const callback = () => {
      setOpen(false);
      resetForm();
    };

    const onFormSubmit = React.useCallback(
      ({ justificationId, reason }: FormSchema) => {
        if (!deliveryVoucher?.id) {
          return console.error('VOUCHER NÃO ENCONTRADO');
        }

        onSubmit(
          {
            deliveryVoucherId: deliveryVoucher?.id!,
            justificationId: Number(justificationId),
            reason,
          },
          callback
        );
      },
      [onSubmit, deliveryVoucher?.id]
    );

    const resetForm = React.useCallback(() => {
      form.reset(
        { justificationId: undefined, reason: '' },
        {
          keepValues: false,
        }
      );
    }, []);

    const onOpenChange = React.useCallback(
      (open: boolean) => {
        //setTimeout resolve o problema do pointerEvents do radix
        setTimeout(() => {
          resetForm();
          setOpen(open);
        }, 0);
      },
      [resetForm]
    );

    React.useImperativeHandle(
      ref,
      () => ({
        open: () => onOpenChange(true),
        close: () => onOpenChange(false),
      }),
      [onOpenChange]
    );

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={'w-full max-w-[540px]'}>
          <DialogHeader className={'mb-6'}>
            <DialogTitle>Adicionar justificativa</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onFormSubmit)}
              className={'w-full'}
            >
              <div className={'flex flex-col gap-4'}>
                <FormField
                  control={form.control}
                  name={'justificationId'}
                  render={({ field }) => (
                    <ComboboxGs
                      label={'Justificativa'}
                      form={form}
                      fieldName={field.name}
                      fieldValue={field.value}
                      content={justificationOptions.singleSelectOptions}
                      loading={justificationOptions.loading}
                      portal={false}
                    />
                  )}
                />
                <FormField
                  control={form.control}
                  name={'reason'}
                  render={({ field }) => (
                    <TextareaGs
                      {...field}
                      label={'Comentário'}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className={'grid grid-cols-2 gap-4 mt-6'}>
                <Button.Root
                  type={'button'}
                  size={'sm'}
                  variant={'secondary'}
                  onClick={() => onOpenChange(false)}
                >
                  Cancelar
                </Button.Root>
                <Button.Root type={'submit'} size={'sm'} disabled={loading}>
                  {loading ? <ActivityIndicator /> : 'Salvar'}
                </Button.Root>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    );
  }
);
RejectionProfDialog.displayName = 'RejectionProfDialog';
